import { useState, useEffect, useMemo, Fragment } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import { Listbox, Transition, Popover } from '@headlessui/react';
import { ChevronDownIcon, ChevronUpIcon, ChevronRightIcon, DocumentArrowDownIcon, CheckIcon } from '@heroicons/react/20/solid';
import { toast } from 'react-toastify';

function Financials() {
  document.title = 'Financials';

  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  // Data state
  const [jobs, setJobs] = useState([]);
  const [kanbanJobs, setKanbanJobs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [deliveries, setDeliveries] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);

  // View state
  const [activeTab, setActiveTab] = useState('overview'); // overview, customers, shops, aging
  const [expandedCustomers, setExpandedCustomers] = useState(new Set());
  const [expandedShops, setExpandedShops] = useState(new Set());
  const [expandedPOs, setExpandedPOs] = useState(new Set());

  // Filter state
  const [filterCustomer, setFilterCustomer] = useState('');
  const [filterShop, setFilterShop] = useState('');
  const [filterAging, setFilterAging] = useState([]);
  const [filterJobType, setFilterJobType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Sort state
  const [sortField, setSortField] = useState('dueDate');
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    const fetchData = async () => {
      if (authLoading) return;
      if (!user) {
        navigate('/login');
        return;
      }

      // Verify user role
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('role')
        .eq('id', user.id)
        .single();

      if (userError || (userData?.role !== 'admin' && userData?.role !== 'owner')) {
        console.error('Access denied:', userError?.message || 'Insufficient permissions');
        navigate('/access-denied');
        return;
      }

      setLoading(true);
      try {
        const [
          { data: jobsData, error: jobsError },
          { data: kanbanJobsData, error: kanbanJobsError },
          { data: invoicesData, error: invoicesError },
          { data: deliveriesData, error: deliveriesError },
          { data: customersData, error: customersError },
          { data: shopsData, error: shopsError },
        ] = await Promise.all([
          // All jobs - kanban status determined by kanban_job_id field
          supabase
            .from('jobs')
            .select(`
              id, po_number, po_line_number, part_number, description,
              expected_delivery_date, quantity_on_order, unit_price, status,
              produced_quantity, shipped_quantity, material_status, material_notes,
              shop_id, customer_id, kanban_job_id,
              shops(id, name),
              customers(id, name, nickname)
            `)
            .not('status', 'eq', 'closed'),
          // Kanban blanket orders - to calculate unreleased revenue
          supabase
            .from('kanban_jobs')
            .select(`
              id, po_number, po_line_number, part_number, description,
              quantity_on_order, unit_price, status,
              customer_id,
              customers(id, name, nickname)
            `)
            .not('status', 'eq', 'closed'),
          supabase
            .from('invoices')
            .select('invoice_number, package_number, total_amount, status, created_at, net_due_date'),
          supabase
            .from('deliveries')
            .select('delivery_id, package_number, po_number, po_line_number, quantity, invoice_number, ship_date, job_id'),
          supabase
            .from('customers')
            .select('id, name, nickname')
            .order('name'),
          supabase
            .from('shops')
            .select('id, name'),
        ]);

        if (jobsError) throw jobsError;
        if (kanbanJobsError) throw kanbanJobsError;
        if (invoicesError) throw invoicesError;
        if (deliveriesError) throw deliveriesError;
        if (customersError) throw customersError;
        if (shopsError) throw shopsError;

        // Mark kanban jobs based on kanban_job_id field
        const allJobs = (jobsData || []).map(job => ({
          ...job,
          isKanbanJob: job.kanban_job_id !== null
        }));
        setJobs(allJobs);
        setKanbanJobs(kanbanJobsData || []);
        setInvoices(invoicesData || []);
        setDeliveries(deliveriesData || []);
        setCustomers(customersData || []);
        setShops(shopsData || []);
      } catch (err) {
        console.error('Error fetching data:', err);
        toast.error(`Failed to load financial data: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [user, authLoading, navigate]);

  // Calculate aging bucket for a date
  const getAgingBucket = (dueDate) => {
    if (!dueDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(dueDate);
    due.setHours(0, 0, 0, 0);
    const diffDays = Math.floor((today - due) / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) return 'current';
    if (diffDays <= 30) return '1-30';
    if (diffDays <= 60) return '31-60';
    if (diffDays <= 90) return '61-90';
    return '90+';
  };

  const getAgingBadge = (bucket) => {
    const styles = {
      'current': 'bg-green-100 text-green-700',
      '1-30': 'bg-yellow-100 text-yellow-700',
      '31-60': 'bg-orange-100 text-orange-700',
      '61-90': 'bg-red-100 text-red-700',
      '90+': 'bg-red-200 text-red-800',
    };
    const labels = {
      'current': 'Current',
      '1-30': '1-30 Days',
      '31-60': '31-60 Days',
      '61-90': '61-90 Days',
      '90+': '90+ Days',
    };
    return { style: styles[bucket] || 'bg-slate-100 text-slate-600', label: labels[bucket] || bucket };
  };

  // Process jobs into financial line items
  const processedData = useMemo(() => {
    // Create delivery lookup
    const shippedByJob = deliveries.reduce((acc, del) => {
      const key = `${del.po_number}-${del.po_line_number}`;
      acc[key] = (acc[key] || 0) + (del.quantity || 0);
      return acc;
    }, {});

    // Create invoice lookup by package
    const invoicesByPackage = invoices.reduce((acc, inv) => {
      if (inv.package_number) {
        acc[inv.package_number] = inv;
      }
      return acc;
    }, {});

    // Track invoiced deliveries
    const invoicedDeliveries = deliveries.filter(d => d.invoice_number);
    const invoicedQtyByJob = invoicedDeliveries.reduce((acc, del) => {
      const key = `${del.po_number}-${del.po_line_number}`;
      acc[key] = (acc[key] || 0) + (del.quantity || 0);
      return acc;
    }, {});

    // Calculate released quantities per kanban_job_id for unreleased calculation
    const releasedByKanbanId = jobs.reduce((acc, job) => {
      if (job.kanban_job_id) {
        acc[job.kanban_job_id] = (acc[job.kanban_job_id] || 0) + (job.quantity_on_order || 0);
      }
      return acc;
    }, {});

    // Create lookup of kanban_jobs by PO+line to filter out legacy duplicates
    const kanbanJobsByPoLine = kanbanJobs.reduce((acc, kj) => {
      const key = `${kj.po_number}-${kj.po_line_number}`;
      acc[key] = true;
      return acc;
    }, {});

    // Process regular jobs (standard POs and work orders)
    const jobItems = jobs
      .filter(job => {
        // Exclude test POs
        if (job.po_number?.includes('TEST') || job.po_number?.includes('JUNK')) return false;
        // Exclude legacy kanban jobs if there's a kanban_jobs entry for same PO+line
        // (legacy = no kanban_job_id AND no due date)
        if (!job.kanban_job_id && !job.expected_delivery_date) {
          const key = `${job.po_number}-${job.po_line_number}`;
          if (kanbanJobsByPoLine[key]) return false;
        }
        return true;
      })
      .map(job => {
        const key = `${job.po_number}-${job.po_line_number}`;
        const shipped = shippedByJob[key] || 0;
        const invoiced = invoicedQtyByJob[key] || 0;
        const balance = (job.quantity_on_order || 0) - shipped;
        const uninvoicedShipped = shipped - invoiced;
        const dollarBalance = balance * (job.unit_price || 0);
        const dollarTotal = (job.quantity_on_order || 0) * (job.unit_price || 0);
        const uninvoicedValue = uninvoicedShipped * (job.unit_price || 0);
        const agingBucket = getAgingBucket(job.expected_delivery_date);
        const isOverdue = agingBucket && agingBucket !== 'current';

        // Determine job type: Work Order (from kanban), Standard (with due date), or Kanban (legacy no due date)
        const jobType = job.isKanbanJob
          ? 'workorder'
          : job.expected_delivery_date
            ? 'standard'
            : 'kanban';

        return {
          id: job.id,
          poNumber: job.po_number,
          lineNumber: job.po_line_number,
          partNumber: job.part_number,
          description: job.description,
          dueDate: job.expected_delivery_date,
          dueDateFormatted: job.expected_delivery_date
            ? new Date(job.expected_delivery_date).toLocaleDateString()
            : '-',
          ordered: job.quantity_on_order || 0,
          shipped,
          balance,
          invoiced,
          uninvoicedShipped,
          unitPrice: job.unit_price || 0,
          dollarBalance,
          dollarTotal,
          uninvoicedValue,
          shopId: job.shop_id,
          shopName: job.shops?.name || '-',
          customerId: job.customer_id,
          customerName: job.customers?.nickname || job.customers?.name || 'Unknown',
          status: job.status,
          jobType,
          agingBucket,
          isOverdue,
        };
      })
      .filter(item => item.balance > 0);

    // Process unreleased kanban blanket orders
    const unreleasedItems = kanbanJobs
      .filter(kj => {
        if (kj.po_number?.includes('TEST') || kj.po_number?.includes('JUNK')) return false;
        const released = releasedByKanbanId[kj.id] || 0;
        const unreleased = (kj.quantity_on_order || 0) - released;
        return unreleased > 0;
      })
      .map(kj => {
        const released = releasedByKanbanId[kj.id] || 0;
        const unreleased = (kj.quantity_on_order || 0) - released;
        const dollarBalance = unreleased * (kj.unit_price || 0);
        const dollarTotal = (kj.quantity_on_order || 0) * (kj.unit_price || 0);

        return {
          id: `kanban-${kj.id}`,
          poNumber: kj.po_number,
          lineNumber: kj.po_line_number,
          partNumber: kj.part_number,
          description: kj.description,
          dueDate: null,
          dueDateFormatted: '-',
          ordered: kj.quantity_on_order || 0,
          shipped: released,
          balance: unreleased,
          invoiced: 0,
          uninvoicedShipped: 0,
          unitPrice: kj.unit_price || 0,
          dollarBalance,
          dollarTotal,
          uninvoicedValue: 0,
          shopId: null,
          shopName: '-',
          customerId: kj.customer_id,
          customerName: kj.customers?.nickname || kj.customers?.name || 'Unknown',
          status: kj.status,
          jobType: 'kanban',
          agingBucket: null,
          isOverdue: false,
        };
      });

    return [...jobItems, ...unreleasedItems];
  }, [jobs, kanbanJobs, deliveries, invoices]);

  // Apply filters
  const filteredData = useMemo(() => {
    let data = [...processedData];

    if (filterCustomer) {
      data = data.filter(d => d.customerId === parseInt(filterCustomer));
    }
    if (filterShop) {
      data = data.filter(d => d.shopId === parseInt(filterShop));
    }
    if (filterAging.length > 0) {
      data = data.filter(d => filterAging.includes(d.agingBucket));
    }
    if (filterJobType) {
      data = data.filter(d => d.jobType === filterJobType);
    }
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      data = data.filter(d =>
        d.poNumber?.toLowerCase().includes(q) ||
        d.partNumber?.toLowerCase().includes(q) ||
        d.customerName?.toLowerCase().includes(q)
      );
    }

    // Sort
    data.sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (sortField === 'dueDate') {
        aVal = a.dueDate ? new Date(a.dueDate) : new Date('9999-12-31');
        bVal = b.dueDate ? new Date(b.dueDate) : new Date('9999-12-31');
      }

      if (typeof aVal === 'number') {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      if (aVal instanceof Date) {
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      return sortDirection === 'asc'
        ? String(aVal || '').localeCompare(String(bVal || ''))
        : String(bVal || '').localeCompare(String(aVal || ''));
    });

    return data;
  }, [processedData, filterCustomer, filterShop, filterAging, filterJobType, searchQuery, sortField, sortDirection]);

  // Group by customer
  const customerGroups = useMemo(() => {
    const groups = {};
    filteredData.forEach(item => {
      const key = item.customerId || 'unknown';
      if (!groups[key]) {
        groups[key] = {
          customerId: item.customerId,
          customerName: item.customerName,
          items: [],
          totalBalance: 0,
          totalOrdered: 0,
          uninvoicedValue: 0,
          overdueCount: 0,
          overdueValue: 0,
        };
      }
      groups[key].items.push(item);
      groups[key].totalBalance += item.dollarBalance;
      groups[key].totalOrdered += item.dollarTotal;
      groups[key].uninvoicedValue += item.uninvoicedValue;
      if (item.isOverdue) {
        groups[key].overdueCount++;
        groups[key].overdueValue += item.dollarBalance;
      }
    });
    return Object.values(groups).sort((a, b) => b.totalBalance - a.totalBalance);
  }, [filteredData]);

  // Group by shop
  const shopGroups = useMemo(() => {
    const groups = {};
    filteredData.forEach(item => {
      const key = item.shopId || 'unknown';
      if (!groups[key]) {
        groups[key] = {
          shopId: item.shopId,
          shopName: item.shopName,
          items: [],
          totalBalance: 0,
          totalOrdered: 0,
          uninvoicedValue: 0,
          overdueCount: 0,
          overdueValue: 0,
        };
      }
      groups[key].items.push(item);
      groups[key].totalBalance += item.dollarBalance;
      groups[key].totalOrdered += item.dollarTotal;
      groups[key].uninvoicedValue += item.uninvoicedValue;
      if (item.isOverdue) {
        groups[key].overdueCount++;
        groups[key].overdueValue += item.dollarBalance;
      }
    });
    return Object.values(groups).sort((a, b) => b.totalBalance - a.totalBalance);
  }, [filteredData]);

  // Group by PO
  const poGroups = useMemo(() => {
    const groups = {};
    filteredData.forEach(item => {
      const key = item.poNumber || 'unknown';
      if (!groups[key]) {
        groups[key] = {
          poNumber: item.poNumber,
          customerName: item.customerName,
          customerId: item.customerId,
          items: [],
          totalBalance: 0,
          totalOrdered: 0,
          uninvoicedValue: 0,
          overdueCount: 0,
          overdueValue: 0,
        };
      }
      groups[key].items.push(item);
      groups[key].totalBalance += item.dollarBalance;
      groups[key].totalOrdered += item.dollarTotal;
      groups[key].uninvoicedValue += item.uninvoicedValue;
      if (item.isOverdue) {
        groups[key].overdueCount++;
        groups[key].overdueValue += item.dollarBalance;
      }
    });
    return Object.values(groups).sort((a, b) => b.totalBalance - a.totalBalance);
  }, [filteredData]);

  // Summary stats
  const stats = useMemo(() => {
    const totalBalance = filteredData.reduce((sum, d) => sum + d.dollarBalance, 0);
    const totalOrdered = filteredData.reduce((sum, d) => sum + d.dollarTotal, 0);
    const uninvoicedValue = filteredData.reduce((sum, d) => sum + d.uninvoicedValue, 0);
    const overdueItems = filteredData.filter(d => d.isOverdue);
    const overdueValue = overdueItems.reduce((sum, d) => sum + d.dollarBalance, 0);

    // Job type breakdown - separate work orders (released) from kanban blankets (unreleased)
    const workorderItems = filteredData.filter(d => d.jobType === 'workorder');
    const kanbanItems = filteredData.filter(d => d.jobType === 'kanban');
    const standardItems = filteredData.filter(d => d.jobType === 'standard');
    const workorderBalance = workorderItems.reduce((sum, d) => sum + d.dollarBalance, 0);
    const unreleasedValue = kanbanItems.reduce((sum, d) => sum + d.dollarBalance, 0);
    const standardBalance = standardItems.reduce((sum, d) => sum + d.dollarBalance, 0);

    // Aging breakdown
    const aging = {
      current: { count: 0, value: 0 },
      '1-30': { count: 0, value: 0 },
      '31-60': { count: 0, value: 0 },
      '61-90': { count: 0, value: 0 },
      '90+': { count: 0, value: 0 },
    };
    filteredData.forEach(d => {
      if (d.agingBucket && aging[d.agingBucket]) {
        aging[d.agingBucket].count++;
        aging[d.agingBucket].value += d.dollarBalance;
      }
    });

    return {
      totalBalance,
      totalOrdered,
      uninvoicedValue,
      overdueCount: overdueItems.length,
      overdueValue,
      workorderBalance,
      workorderCount: workorderItems.length,
      standardBalance,
      standardCount: standardItems.length,
      unreleasedValue,
      unreleasedCount: kanbanItems.length,
      uniqueCustomers: new Set(filteredData.map(d => d.customerId)).size,
      uniquePOs: new Set(filteredData.map(d => d.poNumber)).size,
      lineItems: filteredData.length,
      aging,
    };
  }, [filteredData]);

  // Handlers
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const toggleCustomerExpanded = (customerId) => {
    setExpandedCustomers(prev => {
      const next = new Set(prev);
      if (next.has(customerId)) {
        next.delete(customerId);
      } else {
        next.add(customerId);
      }
      return next;
    });
  };

  const toggleShopExpanded = (shopId) => {
    setExpandedShops(prev => {
      const next = new Set(prev);
      if (next.has(shopId)) {
        next.delete(shopId);
      } else {
        next.add(shopId);
      }
      return next;
    });
  };

  const togglePoExpanded = (poNumber) => {
    setExpandedPOs(prev => {
      const next = new Set(prev);
      if (next.has(poNumber)) {
        next.delete(poNumber);
      } else {
        next.add(poNumber);
      }
      return next;
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const exportCSV = () => {
    const headers = ['Customer', 'PO', 'Line', 'Part', 'Due Date', 'Ordered', 'Shipped', 'Balance', 'Unit Price', '$ Balance', 'Type', 'Aging'];
    const rows = filteredData.map(d => [
      d.customerName,
      d.poNumber,
      d.lineNumber,
      d.partNumber,
      d.dueDateFormatted,
      d.ordered,
      d.shipped,
      d.balance,
      d.unitPrice,
      d.dollarBalance,
      getJobTypeBadge(d.jobType).label,
      d.agingBucket || '',
    ]);

    const csv = [headers, ...rows].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `financials-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exported');
  };

  const clearFilters = () => {
    setFilterCustomer('');
    setFilterShop('');
    setFilterAging([]);
    setFilterJobType('');
    setSearchQuery('');
  };

  const hasActiveFilters = filterCustomer || filterShop || filterAging.length > 0 || filterJobType || searchQuery;

  // Dropdown options
  const customerOptions = [
    { value: '', label: 'All Customers' },
    ...customers.map(c => ({ value: String(c.id), label: c.nickname || c.name }))
  ];

  const shopOptions = [
    { value: '', label: 'All Shops' },
    ...shops.map(s => ({ value: String(s.id), label: s.name }))
  ];

  const agingOptions = [
    { value: 'current', label: 'Current' },
    { value: '1-30', label: '1-30 Days' },
    { value: '31-60', label: '31-60 Days' },
    { value: '61-90', label: '61-90 Days' },
    { value: '90+', label: '90+ Days' },
  ];

  const jobTypeOptions = [
    { value: '', label: 'All Types' },
    { value: 'standard', label: 'Standard' },
    { value: 'workorder', label: 'Work Order' },
    { value: 'kanban', label: 'Kanban' },
  ];

  const getJobTypeBadge = (jobType) => {
    const styles = {
      'standard': 'bg-slate-100 text-slate-700',
      'workorder': 'bg-blue-100 text-blue-700',
      'kanban': 'bg-purple-100 text-purple-700',
    };
    const labels = {
      'standard': 'Standard',
      'workorder': 'Work Order',
      'kanban': 'Kanban',
    };
    return { style: styles[jobType] || 'bg-slate-100 text-slate-600', label: labels[jobType] || jobType };
  };

  // Custom dropdown component
  const FilterDropdown = ({ value, onChange, options, className = '' }) => {
    const selected = options.find(o => o.value === value) || options[0];
    return (
      <Listbox value={value} onChange={onChange}>
        <div className={`relative ${className}`}>
          <Listbox.Button className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors bg-white min-w-[120px] justify-between h-8">
            <span className={value ? 'text-slate-800' : 'text-slate-500'}>
              {selected.label}
            </span>
            <ChevronDownIcon className="w-4 h-4 text-slate-400" />
          </Listbox.Button>
          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Listbox.Options className="absolute z-50 mt-1 max-h-60 w-full min-w-[160px] overflow-auto rounded-sm bg-white py-1 text-sm shadow-lg ring-1 ring-slate-200 focus:outline-none">
              {options.map((option) => (
                <Listbox.Option
                  key={option.value}
                  value={option.value}
                  className={({ active }) =>
                    `relative cursor-pointer select-none py-1.5 pl-8 pr-4 ${
                      active ? 'bg-slate-100 text-slate-900' : 'text-slate-700'
                    }`
                  }
                >
                  {({ selected }) => (
                    <>
                      <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                        {option.label}
                      </span>
                      {selected && (
                        <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-slate-600">
                          <CheckIcon className="h-4 w-4" aria-hidden="true" />
                        </span>
                      )}
                    </>
                  )}
                </Listbox.Option>
              ))}
            </Listbox.Options>
          </Transition>
        </div>
      </Listbox>
    );
  };

  // Multi-select dropdown component for aging (stays open on selection)
  const MultiSelectDropdown = ({ value, onChange, options, placeholder, className = '' }) => {
    const toggleOption = (optionValue) => {
      if (value.includes(optionValue)) {
        onChange(value.filter(v => v !== optionValue));
      } else {
        onChange([...value, optionValue]);
      }
    };

    const selectAll = () => {
      onChange([]);
    };

    const isAllSelected = value.length === 0;

    return (
      <Popover className={`relative ${className}`}>
        <Popover.Button className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors bg-white min-w-[120px] justify-between h-8 focus:outline-none focus:ring-1 focus:ring-slate-400">
          <span className={value.length > 0 ? 'text-slate-800' : 'text-slate-500'}>
            {value.length > 0 ? `${value.length} selected` : placeholder}
          </span>
          <ChevronDownIcon className="w-4 h-4 text-slate-400" />
        </Popover.Button>
        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="opacity-0 translate-y-1"
          enterTo="opacity-100 translate-y-0"
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100 translate-y-0"
          leaveTo="opacity-0 translate-y-1"
        >
          <Popover.Panel className="absolute z-50 mt-1 w-full min-w-[160px] rounded-sm bg-white py-1 text-sm shadow-lg ring-1 ring-slate-200 focus:outline-none">
            {/* All option */}
            <div
              onClick={selectAll}
              className={`relative cursor-pointer select-none py-1.5 pl-8 pr-4 hover:bg-slate-100 ${
                isAllSelected ? 'text-slate-900 font-medium' : 'text-slate-700'
              }`}
            >
              <span>All</span>
              {isAllSelected && (
                <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-slate-600">
                  <CheckIcon className="h-4 w-4" aria-hidden="true" />
                </span>
              )}
            </div>
            <div className="border-t border-slate-100 my-1" />
            {/* Individual options */}
            {options.map((option) => {
              const isSelected = value.includes(option.value);
              return (
                <div
                  key={option.value}
                  onClick={() => toggleOption(option.value)}
                  className={`relative cursor-pointer select-none py-1.5 pl-8 pr-4 hover:bg-slate-100 ${
                    isSelected ? 'text-slate-900 font-medium' : 'text-slate-700'
                  }`}
                >
                  <span>{option.label}</span>
                  {isSelected && (
                    <span className="absolute inset-y-0 left-0 flex items-center pl-2 text-slate-600">
                      <CheckIcon className="h-4 w-4" aria-hidden="true" />
                    </span>
                  )}
                </div>
              );
            })}
          </Popover.Panel>
        </Transition>
      </Popover>
    );
  };

  if (authLoading || loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <p className="text-slate-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Header with inline filters */}
      <div className="bg-white border-b border-slate-200 px-4 py-2">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold text-slate-800">Financials</h1>
            <span className="text-xs text-slate-400">{stats.lineItems} items</span>
          </div>

          <div className="flex-1" />

          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search..."
            className="w-40 px-2.5 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 bg-slate-50 h-8"
          />

          <FilterDropdown
            value={filterCustomer}
            onChange={setFilterCustomer}
            options={customerOptions}
          />

          <FilterDropdown
            value={filterShop}
            onChange={setFilterShop}
            options={shopOptions}
          />

          <MultiSelectDropdown
            value={filterAging}
            onChange={setFilterAging}
            options={agingOptions}
            placeholder="All Aging"
          />

          <FilterDropdown
            value={filterJobType}
            onChange={setFilterJobType}
            options={jobTypeOptions}
          />

          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="px-2 py-1 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-sm"
            >
              Clear
            </button>
          )}

          <button
            onClick={exportCSV}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 rounded-sm text-slate-600 hover:bg-slate-50 transition-colors h-8"
          >
            <DocumentArrowDownIcon className="w-4 h-4" />
            Export
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white border-b border-slate-200 px-4">
        <div className="flex gap-6">
          {[
            { key: 'overview', label: 'Overview' },
            { key: 'customers', label: 'By Customer' },
            { key: 'pos', label: 'By PO' },
            { key: 'shops', label: 'By Shop' },
            { key: 'aging', label: 'Aging Report' },
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-slate-800 text-slate-800'
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="p-4">
        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3 mb-4">
          <div className="bg-white rounded-sm border border-slate-200 p-3">
            <p className="text-xs text-slate-500">Total Outstanding</p>
            <p className="text-xl font-semibold text-slate-800 tabular-nums">{formatCurrency(stats.totalBalance)}</p>
            <p className="text-xs text-slate-400">{stats.lineItems} items</p>
          </div>
          <div className="bg-white rounded-sm border border-slate-200 p-3">
            <p className="text-xs text-slate-500">Standard POs</p>
            <p className="text-xl font-semibold text-slate-800 tabular-nums">{formatCurrency(stats.standardBalance)}</p>
            <p className="text-xs text-slate-400">{stats.standardCount} items</p>
          </div>
          <div className="bg-white rounded-sm border border-slate-200 p-3">
            <p className="text-xs text-slate-500">Kanban Released</p>
            <p className="text-xl font-semibold text-blue-600 tabular-nums">{formatCurrency(stats.workorderBalance)}</p>
            <p className="text-xs text-slate-400">{stats.workorderCount} work orders</p>
          </div>
          <div className="bg-white rounded-sm border border-slate-200 p-3">
            <p className="text-xs text-slate-500">Kanban Unreleased</p>
            <p className="text-xl font-semibold text-purple-600 tabular-nums">{formatCurrency(stats.unreleasedValue)}</p>
            <p className="text-xs text-slate-400">{stats.unreleasedCount} blankets</p>
          </div>
          <div className="bg-white rounded-sm border border-slate-200 p-3">
            <p className="text-xs text-slate-500">Overdue Value</p>
            <p className="text-xl font-semibold text-red-600 tabular-nums">{formatCurrency(stats.overdueValue)}</p>
            <p className="text-xs text-slate-400">{stats.overdueCount} items</p>
          </div>
          <div className="bg-white rounded-sm border border-slate-200 p-3">
            <p className="text-xs text-slate-500">Shipped Uninvoiced</p>
            <p className="text-xl font-semibold text-amber-600 tabular-nums">{formatCurrency(stats.uninvoicedValue)}</p>
          </div>
          <div className="bg-white rounded-sm border border-slate-200 p-3">
            <p className="text-xs text-slate-500">Active Customers</p>
            <p className="text-xl font-semibold text-slate-800 tabular-nums">{stats.uniqueCustomers}</p>
          </div>
          <div className="bg-white rounded-sm border border-slate-200 p-3">
            <p className="text-xs text-slate-500">Open POs</p>
            <p className="text-xl font-semibold text-slate-800 tabular-nums">{stats.uniquePOs}</p>
          </div>
        </div>

        {/* Overview Tab - Line Items Table */}
        {activeTab === 'overview' && (
          <div className="bg-white rounded-sm border border-slate-200">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100" onClick={() => handleSort('customerName')}>
                      Customer {sortField === 'customerName' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3 h-3 inline" /> : <ChevronDownIcon className="w-3 h-3 inline" />)}
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100" onClick={() => handleSort('poNumber')}>
                      PO {sortField === 'poNumber' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3 h-3 inline" /> : <ChevronDownIcon className="w-3 h-3 inline" />)}
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Ln</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100" onClick={() => handleSort('partNumber')}>
                      Part {sortField === 'partNumber' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3 h-3 inline" /> : <ChevronDownIcon className="w-3 h-3 inline" />)}
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100" onClick={() => handleSort('dueDate')}>
                      Due {sortField === 'dueDate' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3 h-3 inline" /> : <ChevronDownIcon className="w-3 h-3 inline" />)}
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Aging</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600 uppercase tracking-wide">Ord</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600 uppercase tracking-wide">Ship</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600 uppercase tracking-wide">Bal</th>
                    <th className="px-3 py-2 text-right text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100" onClick={() => handleSort('dollarBalance')}>
                      $ Bal {sortField === 'dollarBalance' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3 h-3 inline" /> : <ChevronDownIcon className="w-3 h-3 inline" />)}
                    </th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Type</th>
                    <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Shop</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredData.map((row) => {
                    const agingBadge = row.agingBucket ? getAgingBadge(row.agingBucket) : null;
                    return (
                      <tr key={row.id} className={`hover:bg-slate-50 transition-colors ${row.isOverdue ? 'bg-red-50' : ''}`}>
                        <td className="px-3 py-2 text-sm text-slate-600 max-w-[120px] truncate">{row.customerName}</td>
                        <td className="px-3 py-2 text-sm">
                          <Link to={`/jobs/${row.poNumber}`} className="font-medium text-slate-700 hover:text-slate-900 hover:underline">
                            {row.poNumber}
                          </Link>
                        </td>
                        <td className="px-3 py-2 text-sm text-slate-600 tabular-nums">{row.lineNumber}</td>
                        <td className="px-3 py-2 text-sm font-mono text-slate-700">{row.partNumber}</td>
                        <td className={`px-3 py-2 text-sm tabular-nums ${row.isOverdue ? 'text-red-700 font-medium' : 'text-slate-600'}`}>
                          {row.dueDateFormatted}
                        </td>
                        <td className="px-3 py-2">
                          {agingBadge && (
                            <span className={`inline-block px-1.5 py-0.5 text-xs font-medium rounded-sm ${agingBadge.style}`}>
                              {agingBadge.label}
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-sm text-slate-600 tabular-nums text-right">{row.ordered}</td>
                        <td className="px-3 py-2 text-sm text-slate-600 tabular-nums text-right">{row.shipped}</td>
                        <td className="px-3 py-2 text-sm font-medium text-slate-800 tabular-nums text-right">{row.balance}</td>
                        <td className="px-3 py-2 text-sm font-medium text-slate-800 tabular-nums text-right">{formatCurrency(row.dollarBalance)}</td>
                        <td className="px-3 py-2">
                          {(() => {
                            const typeBadge = getJobTypeBadge(row.jobType);
                            return (
                              <span className={`inline-block px-1.5 py-0.5 text-xs font-medium rounded-sm ${typeBadge.style}`}>
                                {typeBadge.label}
                              </span>
                            );
                          })()}
                        </td>
                        <td className="px-3 py-2 text-sm text-slate-600">{row.shopName}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {filteredData.length === 0 && (
              <div className="px-4 py-8 text-center text-slate-500">
                No outstanding orders found.
              </div>
            )}
          </div>
        )}

        {/* Customers Tab */}
        {activeTab === 'customers' && (
          <div className="space-y-3">
            {customerGroups.map(group => {
              const isExpanded = expandedCustomers.has(group.customerId);
              return (
                <div key={group.customerId} className="bg-white rounded-sm border border-slate-200">
                  <div
                    className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => toggleCustomerExpanded(group.customerId)}
                  >
                    <div className="flex items-center gap-3">
                      <ChevronRightIcon className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                      <div>
                        <p className="font-medium text-slate-800">{group.customerName}</p>
                        <p className="text-xs text-slate-500">{group.items.length} line items</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6 text-right">
                      <div>
                        <p className="text-xs text-slate-500">Outstanding</p>
                        <p className="text-sm font-semibold text-slate-800 tabular-nums">{formatCurrency(group.totalBalance)}</p>
                      </div>
                      {group.overdueCount > 0 && (
                        <div>
                          <p className="text-xs text-slate-500">Overdue</p>
                          <p className="text-sm font-semibold text-red-600 tabular-nums">{formatCurrency(group.overdueValue)}</p>
                        </div>
                      )}
                      {group.uninvoicedValue > 0 && (
                        <div>
                          <p className="text-xs text-slate-500">Uninvoiced</p>
                          <p className="text-sm font-semibold text-amber-600 tabular-nums">{formatCurrency(group.uninvoicedValue)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="border-t border-slate-200">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-slate-50">
                            <th className="px-4 py-1.5 text-left text-xs font-medium text-slate-500">PO</th>
                            <th className="px-4 py-1.5 text-left text-xs font-medium text-slate-500">Ln</th>
                            <th className="px-4 py-1.5 text-left text-xs font-medium text-slate-500">Part</th>
                            <th className="px-4 py-1.5 text-left text-xs font-medium text-slate-500">Due</th>
                            <th className="px-4 py-1.5 text-left text-xs font-medium text-slate-500">Aging</th>
                            <th className="px-4 py-1.5 text-right text-xs font-medium text-slate-500">Balance</th>
                            <th className="px-4 py-1.5 text-right text-xs font-medium text-slate-500">$ Balance</th>
                            <th className="px-4 py-1.5 text-left text-xs font-medium text-slate-500">Type</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {group.items.map(item => {
                            const agingBadge = item.agingBucket ? getAgingBadge(item.agingBucket) : null;
                            const typeBadge = getJobTypeBadge(item.jobType);
                            return (
                              <tr key={item.id} className={`hover:bg-slate-50 ${item.isOverdue ? 'bg-red-50' : ''}`}>
                                <td className="px-4 py-2 text-sm">
                                  <Link to={`/jobs/${item.poNumber}`} className="text-slate-700 hover:underline">
                                    {item.poNumber}
                                  </Link>
                                </td>
                                <td className="px-4 py-2 text-sm text-slate-600 tabular-nums">{item.lineNumber}</td>
                                <td className="px-4 py-2 text-sm font-mono text-slate-700">{item.partNumber}</td>
                                <td className={`px-4 py-2 text-sm ${item.isOverdue ? 'text-red-700' : 'text-slate-600'}`}>
                                  {item.dueDateFormatted}
                                </td>
                                <td className="px-4 py-2">
                                  {agingBadge && (
                                    <span className={`inline-block px-1.5 py-0.5 text-xs font-medium rounded-sm ${agingBadge.style}`}>
                                      {agingBadge.label}
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-2 text-sm text-slate-700 tabular-nums text-right">{item.balance}</td>
                                <td className="px-4 py-2 text-sm font-medium text-slate-800 tabular-nums text-right">{formatCurrency(item.dollarBalance)}</td>
                                <td className="px-4 py-2">
                                  <span className={`inline-block px-1.5 py-0.5 text-xs font-medium rounded-sm ${typeBadge.style}`}>
                                    {typeBadge.label}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
            {customerGroups.length === 0 && (
              <div className="bg-white rounded-sm border border-slate-200 px-4 py-8 text-center text-slate-500">
                No customers with outstanding orders.
              </div>
            )}
          </div>
        )}

        {/* POs Tab */}
        {activeTab === 'pos' && (
          <div className="space-y-3">
            {poGroups.map(group => {
              const isExpanded = expandedPOs.has(group.poNumber);
              return (
                <div key={group.poNumber} className="bg-white rounded-sm border border-slate-200">
                  <div
                    className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => togglePoExpanded(group.poNumber)}
                  >
                    <div className="flex items-center gap-3">
                      <ChevronRightIcon className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                      <div>
                        <Link to={`/jobs/${group.poNumber}`} className="font-medium text-slate-800 hover:underline" onClick={(e) => e.stopPropagation()}>
                          {group.poNumber}
                        </Link>
                        <p className="text-xs text-slate-500">{group.customerName} • {group.items.length} line items</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6 text-right">
                      <div>
                        <p className="text-xs text-slate-500">Outstanding</p>
                        <p className="text-sm font-semibold text-slate-800 tabular-nums">{formatCurrency(group.totalBalance)}</p>
                      </div>
                      {group.overdueCount > 0 && (
                        <div>
                          <p className="text-xs text-slate-500">Overdue</p>
                          <p className="text-sm font-semibold text-red-600 tabular-nums">{formatCurrency(group.overdueValue)}</p>
                        </div>
                      )}
                      {group.uninvoicedValue > 0 && (
                        <div>
                          <p className="text-xs text-slate-500">Uninvoiced</p>
                          <p className="text-sm font-semibold text-amber-600 tabular-nums">{formatCurrency(group.uninvoicedValue)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="border-t border-slate-200">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-slate-50">
                            <th className="px-4 py-1.5 text-left text-xs font-medium text-slate-500">Ln</th>
                            <th className="px-4 py-1.5 text-left text-xs font-medium text-slate-500">Part</th>
                            <th className="px-4 py-1.5 text-left text-xs font-medium text-slate-500">Due</th>
                            <th className="px-4 py-1.5 text-left text-xs font-medium text-slate-500">Aging</th>
                            <th className="px-4 py-1.5 text-right text-xs font-medium text-slate-500">Balance</th>
                            <th className="px-4 py-1.5 text-right text-xs font-medium text-slate-500">$ Balance</th>
                            <th className="px-4 py-1.5 text-left text-xs font-medium text-slate-500">Type</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {group.items.map(item => {
                            const agingBadge = item.agingBucket ? getAgingBadge(item.agingBucket) : null;
                            const typeBadge = getJobTypeBadge(item.jobType);
                            return (
                              <tr key={item.id} className={`hover:bg-slate-50 ${item.isOverdue ? 'bg-red-50' : ''}`}>
                                <td className="px-4 py-2 text-sm text-slate-600 tabular-nums">{item.lineNumber}</td>
                                <td className="px-4 py-2 text-sm font-mono text-slate-700">{item.partNumber}</td>
                                <td className={`px-4 py-2 text-sm ${item.isOverdue ? 'text-red-700' : 'text-slate-600'}`}>
                                  {item.dueDateFormatted}
                                </td>
                                <td className="px-4 py-2">
                                  {agingBadge && (
                                    <span className={`inline-block px-1.5 py-0.5 text-xs font-medium rounded-sm ${agingBadge.style}`}>
                                      {agingBadge.label}
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-2 text-sm text-slate-700 tabular-nums text-right">{item.balance}</td>
                                <td className="px-4 py-2 text-sm font-medium text-slate-800 tabular-nums text-right">{formatCurrency(item.dollarBalance)}</td>
                                <td className="px-4 py-2">
                                  <span className={`inline-block px-1.5 py-0.5 text-xs font-medium rounded-sm ${typeBadge.style}`}>
                                    {typeBadge.label}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
            {poGroups.length === 0 && (
              <div className="bg-white rounded-sm border border-slate-200 px-4 py-8 text-center text-slate-500">
                No POs with outstanding orders.
              </div>
            )}
          </div>
        )}

        {/* Shops Tab */}
        {activeTab === 'shops' && (
          <div className="space-y-3">
            {shopGroups.map(group => {
              const isExpanded = expandedShops.has(group.shopId);
              return (
                <div key={group.shopId} className="bg-white rounded-sm border border-slate-200">
                  <div
                    className="px-4 py-3 flex items-center justify-between cursor-pointer hover:bg-slate-50 transition-colors"
                    onClick={() => toggleShopExpanded(group.shopId)}
                  >
                    <div className="flex items-center gap-3">
                      <ChevronRightIcon className={`w-5 h-5 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                      <div>
                        <p className="font-medium text-slate-800">{group.shopName}</p>
                        <p className="text-xs text-slate-500">{group.items.length} line items</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-6 text-right">
                      <div>
                        <p className="text-xs text-slate-500">Outstanding</p>
                        <p className="text-sm font-semibold text-slate-800 tabular-nums">{formatCurrency(group.totalBalance)}</p>
                      </div>
                      {group.overdueCount > 0 && (
                        <div>
                          <p className="text-xs text-slate-500">Overdue</p>
                          <p className="text-sm font-semibold text-red-600 tabular-nums">{formatCurrency(group.overdueValue)}</p>
                        </div>
                      )}
                      {group.uninvoicedValue > 0 && (
                        <div>
                          <p className="text-xs text-slate-500">Uninvoiced</p>
                          <p className="text-sm font-semibold text-amber-600 tabular-nums">{formatCurrency(group.uninvoicedValue)}</p>
                        </div>
                      )}
                    </div>
                  </div>
                  {isExpanded && (
                    <div className="border-t border-slate-200">
                      <table className="w-full">
                        <thead>
                          <tr className="bg-slate-50">
                            <th className="px-4 py-1.5 text-left text-xs font-medium text-slate-500">Customer</th>
                            <th className="px-4 py-1.5 text-left text-xs font-medium text-slate-500">PO</th>
                            <th className="px-4 py-1.5 text-left text-xs font-medium text-slate-500">Ln</th>
                            <th className="px-4 py-1.5 text-left text-xs font-medium text-slate-500">Part</th>
                            <th className="px-4 py-1.5 text-left text-xs font-medium text-slate-500">Due</th>
                            <th className="px-4 py-1.5 text-left text-xs font-medium text-slate-500">Aging</th>
                            <th className="px-4 py-1.5 text-right text-xs font-medium text-slate-500">Balance</th>
                            <th className="px-4 py-1.5 text-right text-xs font-medium text-slate-500">$ Balance</th>
                            <th className="px-4 py-1.5 text-left text-xs font-medium text-slate-500">Type</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {group.items.map(item => {
                            const agingBadge = item.agingBucket ? getAgingBadge(item.agingBucket) : null;
                            const typeBadge = getJobTypeBadge(item.jobType);
                            return (
                              <tr key={item.id} className={`hover:bg-slate-50 ${item.isOverdue ? 'bg-red-50' : ''}`}>
                                <td className="px-4 py-2 text-sm text-slate-600">{item.customerName}</td>
                                <td className="px-4 py-2 text-sm">
                                  <Link to={`/jobs/${item.poNumber}`} className="text-slate-700 hover:underline">
                                    {item.poNumber}
                                  </Link>
                                </td>
                                <td className="px-4 py-2 text-sm text-slate-600 tabular-nums">{item.lineNumber}</td>
                                <td className="px-4 py-2 text-sm font-mono text-slate-700">{item.partNumber}</td>
                                <td className={`px-4 py-2 text-sm ${item.isOverdue ? 'text-red-700' : 'text-slate-600'}`}>
                                  {item.dueDateFormatted}
                                </td>
                                <td className="px-4 py-2">
                                  {agingBadge && (
                                    <span className={`inline-block px-1.5 py-0.5 text-xs font-medium rounded-sm ${agingBadge.style}`}>
                                      {agingBadge.label}
                                    </span>
                                  )}
                                </td>
                                <td className="px-4 py-2 text-sm text-slate-700 tabular-nums text-right">{item.balance}</td>
                                <td className="px-4 py-2 text-sm font-medium text-slate-800 tabular-nums text-right">{formatCurrency(item.dollarBalance)}</td>
                                <td className="px-4 py-2">
                                  <span className={`inline-block px-1.5 py-0.5 text-xs font-medium rounded-sm ${typeBadge.style}`}>
                                    {typeBadge.label}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              );
            })}
            {shopGroups.length === 0 && (
              <div className="bg-white rounded-sm border border-slate-200 px-4 py-8 text-center text-slate-500">
                No shops with outstanding orders.
              </div>
            )}
          </div>
        )}

        {/* Aging Tab */}
        {activeTab === 'aging' && (
          <div className="space-y-4">
            {['90+', '61-90', '31-60', '1-30', 'current'].map(bucket => {
              const items = filteredData.filter(d => d.agingBucket === bucket);
              const total = items.reduce((sum, d) => sum + d.dollarBalance, 0);
              const badge = getAgingBadge(bucket);
              if (items.length === 0) return null;

              return (
                <div key={bucket} className="bg-white rounded-sm border border-slate-200">
                  <div className="px-4 py-3 border-b border-slate-100 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 text-sm font-medium rounded-sm ${badge.style}`}>
                        {badge.label}
                      </span>
                      <span className="text-sm text-slate-500">{items.length} items</span>
                    </div>
                    <p className="text-lg font-semibold text-slate-800 tabular-nums">{formatCurrency(total)}</p>
                  </div>
                  <table className="w-full table-fixed">
                    <colgroup>
                      <col style={{ width: '18%' }} />
                      <col style={{ width: '14%' }} />
                      <col style={{ width: '6%' }} />
                      <col style={{ width: '22%' }} />
                      <col style={{ width: '12%' }} />
                      <col style={{ width: '12%' }} />
                      <col style={{ width: '16%' }} />
                    </colgroup>
                    <thead>
                      <tr className="bg-slate-50">
                        <th className="px-4 py-1.5 text-left text-xs font-medium text-slate-500">Customer</th>
                        <th className="px-4 py-1.5 text-left text-xs font-medium text-slate-500">PO</th>
                        <th className="px-4 py-1.5 text-left text-xs font-medium text-slate-500">Ln</th>
                        <th className="px-4 py-1.5 text-left text-xs font-medium text-slate-500">Part</th>
                        <th className="px-4 py-1.5 text-left text-xs font-medium text-slate-500">Due Date</th>
                        <th className="px-4 py-1.5 text-right text-xs font-medium text-slate-500">Balance</th>
                        <th className="px-4 py-1.5 text-right text-xs font-medium text-slate-500">$ Balance</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {items.map(item => (
                        <tr key={item.id} className="hover:bg-slate-50">
                          <td className="px-4 py-2 text-sm text-slate-600 truncate">{item.customerName}</td>
                          <td className="px-4 py-2 text-sm truncate">
                            <Link to={`/jobs/${item.poNumber}`} className="text-slate-700 hover:underline">
                              {item.poNumber}
                            </Link>
                          </td>
                          <td className="px-4 py-2 text-sm text-slate-600 tabular-nums">{item.lineNumber}</td>
                          <td className="px-4 py-2 text-sm font-mono text-slate-700 truncate">{item.partNumber}</td>
                          <td className="px-4 py-2 text-sm text-slate-600">
                            {item.dueDateFormatted}
                          </td>
                          <td className="px-4 py-2 text-sm text-slate-700 tabular-nums text-right">{item.balance}</td>
                          <td className="px-4 py-2 text-sm font-medium text-slate-800 tabular-nums text-right">{formatCurrency(item.dollarBalance)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default Financials;
